import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import collabDocumentsApi from "../../../api/collabDocumentsApi";
import collaborationApi from "../../../api/collaborationApi";

const CollabDocuments = ({ collabId, collaborationName }) => {
  const [collabDetails, setCollabDetails] = useState(null);
  const [docData, setDocData] = useState({
    description: "",
    file: null,
    collabName: collaborationName || "",
    sendTo: "",
    role: "",
    email: "",
  });
  const [documents, setDocuments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const senderEmail = user?.userEmail;
  const [contributors, setContributors] = useState([]);

  // Fetch collaboration details
  useEffect(() => {
    const fetchCollabBrief = async () => {
      try {
        const response = await collaborationApi.getCollaBriefById(collabId);
        setCollabDetails(response.data);
      } catch (err) {
        console.error("Error fetching collab brief:", err);
      }
    };
    if (collabId) fetchCollabBrief();
  }, [collabId]);

  useEffect(() => {
    if (collabId) {
      fetchDocuments();
    }
  }, [collabId]);
  // Fetch documents
  const fetchDocuments = async () => {
    try {
      const response =
        await collabDocumentsApi.getCollabDocsByCollabId(collabId);

      if (response.status === 200) {
        setDocuments(response.data);
      } else if (response.status === 204) {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };
  // Fetch contributors
  useEffect(() => {
    if (collabId) {
      collaborationApi
        .getContributorsDetails(collabId)
        .then((response) => {
          const contributorsData = response.data.map((item) => ({
            id: item[0],
            name: item[1],
            email: item[2],
            role: item[3],
          }));
          setContributors(contributorsData);
        })
        .catch((err) => console.error("Failed to load contributors.", err));
    }
  }, [collabId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDocData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setDocData({ ...docData, file: e.target.files[0] });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!collabId) return alert("Select a collaboration first");

    const emailArray = docData.email ? [docData.email] : [];
    const payload = {
      documentName: docData.documentName,
      description: docData.description,
      uploadedBy: senderEmail,
      collabId,
      collabName: collaborationName,
      sendTo: docData.role,
      email: emailArray,
    };

    const formDataToSend = new FormData();
    formDataToSend.append(
      "data",
      new Blob([JSON.stringify(payload)], { type: "application/json" }),
    );

    // If editing and no new file, use existing blob
    if (docData.id && !docData.file && docData.existingFileData) {
      const byteCharacters = atob(docData.existingFileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const existingFile = new File([byteArray], docData.documentName, {
        type: docData.fileType || "application/octet-stream",
      });
      formDataToSend.append("file", existingFile);
    }

    if (docData.file) formDataToSend.append("file", docData.file);

    try {
      let response;
      if (docData.id) {
        response = await collabDocumentsApi.updateDocument(
          docData.id,
          formDataToSend,
        );
        setDocuments((prev) =>
          prev.map((d) => (d.id === docData.id ? response.data : d)),
        );
      } else {
        response = await collabDocumentsApi.uploadDocument(formDataToSend);
        setDocuments((prev) => [...prev, response.data]);
      }

      Swal.fire({
        icon: "success",
        title: docData.id ? "Document Updated!" : "Document Saved!",
      });

      setDocData({
        description: "",
        file: null,
        collabName: collaborationName,
        sendTo: "",
        role: "",
        email: "",
        existingFileData: null,
      });
      setShowForm(false);
    } catch (error) {
      console.error("Error saving document:", error);
      Swal.fire({ icon: "error", title: "Failed to save document" });
    }
  };

  const getFilteredEmails = (role) =>
    contributors.filter((u) => u.role === role).map((u) => u.email);

  const handleEdit = (doc) => {
    if (!doc.documentName) return;

    const filePath = `/templates/${doc.documentName}`; // file in public folder
    fetch(filePath)
      .then((res) => res.blob())
      .then((blob) => {
        const fileType = doc.documentName.endsWith(".pdf")
          ? "application/pdf"
          : doc.documentName.endsWith(".docx")
            ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            : blob.type || "application/octet-stream";

        const fileObj = new File([blob], doc.documentName, { type: fileType });

        setDocData({
          id: doc.id,
          documentName: doc.documentName,
          description: doc.description,
          sendTo: doc.sendTo,
          role: doc.sendTo,
          email: Array.isArray(doc.email) ? doc.email[0] : doc.email,
          file: fileObj,
          collabName: doc.collabName,
          uploadedBy: doc.uploadedBy,
          existingFileData: null,
          fileType,
        });

        setShowForm(true);
      })
      .catch((err) => {
        console.error("Failed to load file from public folder:", err);
        Swal.fire({
          icon: "error",
          title: "File Load Failed",
          text: "Cannot load file from public folder",
        });
      });
  };
  const downloadFile = (doc) => {
    const byteCharacters = atob(doc.file);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: doc.fileType });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = doc.documentName;
    link.click();
  };

  const handleRemove = (id) => {
    collabDocumentsApi
      .updateStatus(id)
      .then((response) => {
        if (response.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Deleted",
            text: "Document removed successfully.",
            confirmButtonText: "OK",
          });

          fetchDocuments();
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong while deleting the document.",
          confirmButtonText: "OK",
        });
        console.error("Error updating document status:", error);
      });
  };
  return (
    <>
      <br />
      <h4 className="mb-2">
        <strong>Documents for {collaborationName}</strong>
      </h4>
      <div className="d-flex align-items-center justify-content-between">
        <h5 className="mb-0">📄 Documents</h5>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? "← Back " : "Upload Document"}
        </Button>
      </div>

      {documents.length === 0 ? (
        <p className="text-muted mb-4">
          No documents uploaded yet. Please upload documents.
        </p>
      ) : (
        !showForm && (
          <>
            <p className="text-black mb-4">List of documents uploaded below.</p>
            <div style={{ overflowX: "auto", width: "100%" }}>
              <Table
                striped
                bordered
                hover
                responsive
                style={{ minWidth: "1100px", whiteSpace: "nowrap" }}
                className="mb-3"
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>File Name</th>
                    <th>Send To</th>
                    <th>Description</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={doc.id}>
                      <td>{index + 1}</td>
                      <td>
                        <Button
                          variant="link"
                          onClick={() => downloadFile(doc)}
                        >
                          {doc.documentName}
                        </Button>
                      </td>

                      <td>
                        {doc.email} - {doc.sendTo}
                      </td>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{doc.description}</Tooltip>}
                        >
                          <span style={{ cursor: "pointer" }}>
                            {doc.description.length > 12
                              ? `${doc.description.slice(0, 12)}...`
                              : doc.description}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>{doc.uploadAt}</td>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="delete-tooltip">Delete</Tooltip>
                          }
                        >
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="me-2"
                            onClick={() => handleRemove(doc.id)}
                          >
                            <FaTrash />
                          </Button>
                        </OverlayTrigger>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEdit(doc)}
                        >
                          ✏️
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )
      )}

      {showForm && (
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Document Name<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={docData.documentName}
                  onChange={handleChange}
                  name="documentName"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Upload Document<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleFileChange}
                  disabled={!collabId}
                />
                {docData.file && (
                  <div className="mt-1">
                    <small className="text-muted">
                      Current file:{" "}
                      <a
                        href={URL.createObjectURL(
                          new File([docData.file], docData.file.name, {
                            type: docData.fileType || docData.file.type,
                          }),
                        )}
                        download={docData.file.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {docData.file.name}
                      </a>
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Send To<span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="sendTo"
                  value={docData.sendTo}
                  onChange={(e) => {
                    const role = e.target.value;
                    handleChange(e);
                    setDocData({
                      ...docData,
                      sendTo: role,
                      role: role,
                      email: "",
                    });
                  }}
                  disabled={!collabId}
                >
                  <option value="">Select a user...</option>
                  <option value="Actor">Actor</option>
                  <option value="Reviewer">Reviewer</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Email<span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="email"
                  value={docData.email}
                  onChange={handleChange}
                  disabled={!collabId}
                >
                  <option value="">Select Email</option>
                  {getFilteredEmails(docData.role).map((email) => (
                    <option key={email} value={email}>
                      {email}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Content / Message / Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={docData.description}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Enter document details..."
                  disabled={!collabId}
                />
              </Form.Group>
            </Col>
          </Row>

          <Button type="submit" variant="info" onClick={handleUpload}>
            💾 Save
          </Button>
        </Form>
      )}
    </>
  );
};

export default CollabDocuments;
